/**
 * Ships in-app debug log entries to the admin panel.
 *
 * Only active when the "logtest" magic key has flipped debug mode on
 * (see `debugMode.js`). Batches up to 200 entries every 15 s OR on
 * pagehide, so the admin team has a near-real-time view of what's
 * happening on the user's TV without overwhelming the backend.
 */
import { API } from "./playerApi";
import { getDebugEntries, subscribeToDebug, isDebugEnabled } from "./debugMode";
import { getDeviceId } from "./deviceId";

const FLUSH_EVERY_MS = 15000;
const MAX_PER_BATCH = 200;
// Crash-safe persistence — entries that haven't reached the backend yet
// are mirrored to localStorage. On every launch we flush this queue
// FIRST so an APK that crashed mid-load (no chance to flush) still
// surfaces its last log lines to the admin panel on the next launch.
const QUEUE_KEY = "zapp_debug_unsent";
const QUEUE_LIMIT = 1000;

let _seenCount = 0; // index of last entry we've already shipped
let _flushing = false;
let _sessionId = null;
let _installed = false;
let _timer = null;
// v1.4.27 — Backoff after auth failures (typically HTTP 401 during
// cold-start before the device has an activation key yet). Prior
// versions kept trying every ~500 ms which blocked the boot fetch
// queue on Android WebView (max 6 concurrent connections). With
// the exponential backoff below the shipper falls silent after the
// second failure and retries at most once every 60 s.
let _authBackoffUntil = 0;
let _consecutiveAuthFailures = 0;

function sessionId() {
  if (_sessionId) return _sessionId;
  _sessionId =
    Math.random().toString(36).slice(2, 8) +
    "-" +
    Date.now().toString(36);
  return _sessionId;
}

function collectDeviceInfo() {
  if (typeof window === "undefined") return {};
  const info = {
    ua: navigator.userAgent || "",
    platform: navigator.platform || "",
    language: navigator.language || "",
    screen_w: window.screen?.width || 0,
    screen_h: window.screen?.height || 0,
    inner_w: window.innerWidth || 0,
    inner_h: window.innerHeight || 0,
    pixel_ratio: window.devicePixelRatio || 1,
  };
  // Form factor — TV vs phone vs tablet vs desktop.
  const ua = info.ua.toLowerCase();
  if (/android tv|googletv|smarttv|smart-tv|smart_tv|hbbtv|webos|tizen|aftn|aftt|aftb|firetv|bravia/.test(ua)) {
    info.form_factor = "tv";
  } else if (/mobile/.test(ua) && /android|iphone/.test(ua)) {
    info.form_factor = "phone";
  } else if (/tablet|ipad/.test(ua) || (info.screen_w >= 768 && info.screen_w < 1280 && /android/.test(ua))) {
    info.form_factor = "tablet";
  } else if (window.electronAPI?.isElectron) {
    info.form_factor = "desktop";
  } else if (info.screen_w >= 1280) {
    info.form_factor = "desktop";
  } else {
    info.form_factor = "unknown";
  }
  // Android version (best-effort from UA)
  const m = ua.match(/android\s+([\d.]+)/);
  if (m) info.android_version = m[1];
  // OEM hint (Samsung/Hisense/TCL/Sony/etc. — common Android TV makers)
  const oemRegex =
    /(samsung|hisense|tcl|sony|bravia|sharp|xiaomi|mi tv|huawei|honor|amazon|aftn|fire tv|lg|skyworth|nvidia shield|chromecast)/;
  const oem = ua.match(oemRegex);
  if (oem) info.manufacturer = oem[1];
  // Best-effort model from UA: take the segment after "Build/" or similar.
  const modelMatch = info.ua.match(/;\s*([^;]+)\s+Build\//);
  if (modelMatch) info.model = modelMatch[1].trim();
  // Device memory hint (Chrome WebView exposes navigator.deviceMemory)
  if (navigator.deviceMemory) info.device_memory_gb = navigator.deviceMemory;
  // Hardware concurrency (cpu cores)
  if (navigator.hardwareConcurrency) info.cpu_cores = navigator.hardwareConcurrency;
  return info;
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function writeQueue(arr) {
  try {
    // Cap so a runaway crash loop can't blow out the 5 MB localStorage
    // quota — we drop the oldest entries first.
    const capped = arr.length > QUEUE_LIMIT ? arr.slice(arr.length - QUEUE_LIMIT) : arr;
    localStorage.setItem(QUEUE_KEY, JSON.stringify(capped));
  } catch {
    /* quota full → silently drop */
  }
}

/** Append fresh entries to the persistent queue. Called every time the
 *  in-memory ring buffer ticks so a sudden process-kill still leaves
 *  the last N entries on disk. */
function persistDelta(allEntries) {
  if (!isDebugEnabled()) return;
  const queue = readQueue();
  const newCount = allEntries.length - _seenCount;
  if (newCount <= 0) return;
  const fresh = allEntries.slice(_seenCount);
  queue.push(...fresh);
  writeQueue(queue);
}

async function flush() {
  if (!isDebugEnabled()) return;
  if (_flushing) return;
  // v1.4.27 — Skip flushes entirely while we're in the auth-failure
  // cool-down. Prevents the boot 401 spam from starving the main
  // playlist-load fetches on the Android WebView connection pool.
  if (Date.now() < _authBackoffUntil) return;
  // Skip if we don't yet have an activation key — the server will
  // return 401 anyway and we'd just burn a network round-trip.
  const activationKey =
    (typeof localStorage !== "undefined" &&
      localStorage.getItem("vupp_activation_key")) ||
    "";
  if (!activationKey) return;
  const queue = readQueue();
  // Also pull anything still buffered in-memory that hasn't been
  // mirrored yet (defensive — `persistDelta` should already have done
  // this, but a race during very fast logging could lag behind).
  const inMem = getDebugEntries();
  if (inMem.length > _seenCount) {
    const fresh = inMem.slice(_seenCount);
    queue.push(...fresh);
    writeQueue(queue);
  }
  if (queue.length === 0) return;
  const batch = queue.slice(0, MAX_PER_BATCH);
  _flushing = true;
  try {
    const deviceId = await getDeviceId();
    const r = await fetch(`${API}/player/debug/log`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Device-ID": deviceId || "",
        "X-Activation-Key": activationKey,
      },
      body: JSON.stringify({
        entries: batch,
        device_info: collectDeviceInfo(),
        app_version: window.__ZAPP_VERSION__ || null,
        session_id: sessionId(),
      }),
    });
    if (r.ok) {
      // Drop the batch we just shipped from the persistent queue, then
      // advance _seenCount so we don't queue them again from in-memory.
      const remaining = queue.slice(batch.length);
      writeQueue(remaining);
      _seenCount = inMem.length;
      _consecutiveAuthFailures = 0;
      _authBackoffUntil = 0;
    } else if (r.status === 401 || r.status === 403) {
      // Auth not ready yet (or the activation key was revoked). Back
      // off aggressively so we don't spam the server + starve the
      // playlist fetches. Exponential up to 60 s.
      _consecutiveAuthFailures += 1;
      const delay = Math.min(
        60_000,
        1000 * Math.pow(2, _consecutiveAuthFailures),
      );
      _authBackoffUntil = Date.now() + delay;
    }
  } catch {
    // Network failure — leave the queue intact for the next attempt
    // (covers both the periodic timer AND the next app launch).
  } finally {
    _flushing = false;
  }
}

/** Boot the shipper. Idempotent — safe to call multiple times. */
export function installDebugLogShipper() {
  if (_installed) return;
  _installed = true;

  // FIRST priority: drain anything that the previous launch (or a
  // crashed launch) left in the persistent queue. We try this whether
  // debug mode is currently on or off — the previous session's logs
  // might be the very thing that tells us why the app died last time.
  setTimeout(() => {
    if (readQueue().length > 0) flush();
  }, 1500);

  // Mirror entries to the persistent queue every time the in-memory
  // ring buffer ticks so a sudden process-kill still leaves the last
  // batch on disk.
  subscribeToDebug((snap) => {
    if (!isDebugEnabled()) return;
    persistDelta(snap);
    if (_timer) return;
    _timer = setTimeout(() => {
      _timer = null;
      flush();
    }, 500);
  });

  // Periodic flush — keeps the admin dashboard fresh even during long
  // log-free stretches.
  setInterval(() => {
    if (isDebugEnabled()) flush();
    else if (readQueue().length > 0) flush(); // ship leftover queue
  }, FLUSH_EVERY_MS);

  // Best-effort final flush before the page is torn down.
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", () => {
      if (isDebugEnabled() || readQueue().length > 0) flush();
    });
  }
}
